import { HttpResponse, HttpErrorResponse } from "@angular/common/http";
import {
  Component,
  OnInit,
  ViewEncapsulation,
  OnDestroy,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { fadeInRightAnimation } from "src/@fury/animations/fade-in-right.animation";
import { fadeInUpAnimation } from "src/@fury/animations/fade-in-up.animation";
import { scaleInAnimation } from "src/@fury/animations/scale-in.animation";
import { Location } from "@angular/common";
import { ApiService } from "src/app/services/api.service";
import { ErrorText } from "src/app/enums/error-text.enum";
import { ErrorType } from "src/app/enums/error-type.enum";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "fury-edit-lead-message",
  templateUrl: "./edit-lead-message.component.html",
  styleUrls: ["./edit-lead-message.component.scss"],
  animations: [fadeInUpAnimation, fadeInRightAnimation, scaleInAnimation],
  encapsulation: ViewEncapsulation.None,
})
export class EditLeadMessageComponent implements OnInit, OnDestroy {
  leadMessageForm!: FormGroup;
  pageName!: string;
  bannerImage: any;
  leadMessageDetails: any;
  editorConfig = {
    allowedContent: false,
    toolbarGroups: [
      { name: "clipboard", groups: ["clipboard", "undo"] },
      { name: "editing", groups: ["find", "selection", "spellchecker"] },
      { name: "links" },
      { name: "insert" },
      { name: "document", groups: ["mode", "document", "doctools"] },
      { name: "basicstyles", groups: ["basicstyles", "cleanup"] },
      { name: "paragraph", groups: ["list", "indent", "blocks"] },
      { name: "styles" },
      { name: "colors" },
    ],
    forcePasteAsPlainText: true,
    image_previewText: " ",
  };
  ngUnsubscribe = new Subject<void>();
  @ViewChild("image_one") public uploadImage: ElementRef;
  constructor(
    private ar: ActivatedRoute,
    public apiService: ApiService,
    private location: Location
  ) {}

  ngOnInit(): void {
    this.formInit();
    this.ar.params.pipe(takeUntil(this.ngUnsubscribe)).subscribe((res: any) => {
      this.pageName = res?.pageName;
      if (this.pageName) {
        this.getLeadMessage();
        this.leadMessageForm.patchValue({
          stateType: res?.pageName,
        });
        if (res.pageName === "not-covered") {
          this.leadMessageForm
            .get("uncoveredStateTopMessage")
            .setValidators(Validators.required);
          this.leadMessageForm
            .get("uncoveredStateBottomMessage")
            .setValidators(Validators.required);
          this.leadMessageForm.get("cpl").setValidators(Validators.required);
          this.leadMessageForm
            .get("url")
            .setValidators([
              Validators.required,
              Validators.pattern(
                /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=%.{}]+$/
              ),
            ]);
          this.leadMessageForm
            .get("secondTimer")
            .setValidators(Validators.required);
        } else if (res.pageName === "covered") {
          this.leadMessageForm
            .get("coveredStateMessage")
            .setValidators(Validators.required);
        }
      }
    });
  }
  formInit() {
    this.leadMessageForm = new FormGroup({
      stateType: new FormControl(""),
      coveredStateMessage: new FormControl(""),
      uncoveredStateTopMessage: new FormControl(""),
      uncoveredStateBottomMessage: new FormControl(""),
      uncoveredStateMessageLogo: new FormControl(""),
      url: new FormControl(""),
      cpl: new FormControl(""),
      secondTimer: new FormControl(""),
      isRedirect: new FormControl(true),
    });
  }

  getLeadMessage() {
    this.apiService
      .get("admin/settings/lead-success-message/get-message")
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: HttpResponse<any>) => {
        if (res.body) {
          this.leadMessageDetails = res.body?.leadSuccessMessage;
          this.leadMessageForm.patchValue({
            uncoveredStateTopMessage:
              res.body?.leadSuccessMessage?.uncoveredStateTopMessage,
            uncoveredStateBottomMessage:
              res.body?.leadSuccessMessage?.uncoveredStateBottomMessage,
            coveredStateMessage:
              res.body?.leadSuccessMessage?.coveredStateMessage,
            cpl: Number(res.body?.leadSuccessMessage?.cpl),
            url: res.body?.leadSuccessMessage?.url,
            secondTimer: res.body?.leadSuccessMessage?.secondTimer,
            isRedirect: res.body?.leadSuccessMessage?.isRedirect,
          });
          this.bannerImage =
            res?.body?.leadSuccessMessage?.uncoveredStateMessageLogo;
        }
      });
  }

  onImageUpload(event: any) {
    if (
      event.target.files[0].type === "image/jpeg" ||
      event.target.files[0].type === "image/png" ||
      event.target.files[0].type === "image/jpg"
    ) {
      let file: File;
      file = event.target.files[0];
      this.leadMessageForm.patchValue({ uncoveredStateMessageLogo: file });
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.bannerImage = e.target.result;
      };
      reader.readAsDataURL(event.target.files[0]);
    } else {
      this.apiService.alert(ErrorText.ImageRestriction, "error");
    }
  }

  removeBadge() {
    if (this.leadMessageDetails?.uncoveredStateMessageLogo) {
      this.apiService
        .delete(`admin/settings/lead-success-message/delete-message-badge`)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          (res: any) => {
            if (res) {
              this.getLeadMessage();
            }
          },
          (err) => {
            this.apiService.alert("Please try again", ErrorType.Error);
          }
        );
    } else {
      this.leadMessageForm.get("uncoveredStateMessageLogo").setValue(null);
      this.bannerImage = null;
      this.uploadImage.nativeElement.value = null;
    }
  }

  update() {
    if (this.leadMessageForm.valid) {
      const data: FormData = new FormData();
      Object.keys(this.leadMessageForm.value).forEach((fieldName) => {
        const value = this.leadMessageForm.value[fieldName];
        if (value !== null && value !== undefined) {
          if (fieldName === "isRedirect") {
            data.append(fieldName, value ? "1" : "0");
          } else {
            data.append(fieldName, value);
          }
        }
      });
      if (this.pageName === "not-covered") {
        data.delete("coveredStateMessage");
      } else if (this.pageName === "covered") {
        data.delete("uncoveredStateBottomMessage");
        data.delete("uncoveredStateTopMessage");
        data.delete("url");
        data.delete("cpl");
        data.delete("secondTimer");
        data.delete("isRedirect");
        data.delete("uncoveredStateMessageLogo");
      }
      this.apiService
        .postMultiData(
          `admin/settings/lead-success-message/update-state-message`,
          data
        )
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe({
          next: (res: HttpResponse<any>) => {
            if (res.body) {
              this.apiService.alert(res.body.message, "success");
              this.location.back();
            } else {
              this.apiService.alert(res.body.message, "warning");
            }
          },
          error: (err: HttpErrorResponse) => {
            if (err) {
              this.apiService.alert(err?.message, "error");
            }
          },
        });
    } else {
      this.leadMessageForm.markAllAsTouched();
    }
  }
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
